'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "className", "onOpenChange", "open", "shrinkOnHide", "style"];
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import { GRID_BREAKPOINT_MEDIUM } from 'HubStyleTokens/sizes';
import { closest } from '../utils/Dom';
import Controllable from '../decorators/Controllable';
import SyntheticEvent from '../core/SyntheticEvent';
import memoizeOne from 'react-utils/memoizeOne';
import { isPageInKeyboardMode } from '../listeners/focusStylesListener';
import { jsx as _jsx } from "react/jsx-runtime";
const getClosestTable = el => closest(el, 'TABLE');
const getClosestTableRow = el => closest(el, 'TR');
const bindEventListeners = instance => {
  // Add event listeners to the row and table this component lives in
  const table = getClosestTable(instance._el);
  const tr = getClosestTableRow(instance._el);
  if (!tr || !table) return;
  table.addEventListener('mouseleave', instance.handleRowOrTableMouseLeave);
  tr.addEventListener('mouseleave', instance.handleRowOrTableMouseLeave);
  tr.addEventListener('mouseenter', instance.handleRowMouseEnter);
  tr.addEventListener('focus', instance.handleRowFocus, true);
  tr.addEventListener('blur', instance.handleRowBlur, true);

  // If the user clicks a control with a mouse, we want to ignore the focus on that control (#3054)
  tr.addEventListener('mouseup', instance.handleRowBlur);
};
const unbindEventListeners = instance => {
  // Clean up any event handlers we've added to other elements
  const table = getClosestTable(instance._el);
  const tr = getClosestTableRow(instance._el);
  if (!tr || !table) return;
  table.removeEventListener('mouseleave', instance.handleRowOrTableMouseLeave);
  tr.removeEventListener('mouseenter', instance.handleRowMouseEnter);
  tr.removeEventListener('mouseleave', instance.handleRowOrTableMouseLeave);
  tr.removeEventListener('focus', instance.handleRowFocus, true);
  tr.removeEventListener('blur', instance.handleRowBlur, true);
  tr.removeEventListener('mouseup', instance.handleRowBlur);
};
const getStyle = (style, top, left) => Object.assign({}, style, {
  top,
  left
});
const Span = styled.span.withConfig({
  displayName: "UITableHoverContent__Span",
  componentId: "sqffdm-0"
})(["@media only screen and (max-width:", "){white-space:nowrap;}opacity:", ";position:", ";&.private-table-hover-content--abs-pos{left:0 !important;position:absolute !important;top:0 !important;}"], GRID_BREAKPOINT_MEDIUM, ({
  open
}) => !open && 0, ({
  shrinkOnHide,
  open
}) => shrinkOnHide && !open && 'absolute');
class UITableHoverContent extends PureComponent {
  constructor(props) {
    super(props);
    this.checkForOpenDropdown = () => {
      if (!this._el) return;
      const rowHasOpenDropdown = !!this._el.querySelector('[data-dropdown-open="true"]');
      if (this.state.rowHasOpenDropdown !== rowHasOpenDropdown) {
        this.setState({
          rowHasOpenDropdown
        });
      }
    };
    this.handleRowMouseEnter = () => {
      this.setState({
        rowHasMouse: true
      });
    };
    this.handleRowOrTableMouseLeave = () => {
      // Sometimes mouseleave is triggered on the <table> but not the <tr>
      if (this.state.rowHasMouse) {
        this.setState({
          rowHasMouse: false
        });
      }
    };
    this.handleRowFocus = () => {
      // Handle focus events only if the user is interacting using the keyboard (#8548)
      this.setState(() => isPageInKeyboardMode() ? {
        rowHasFocus: true
      } : null);
    };
    this.handleRowBlur = () => {
      this.setState({
        rowHasFocus: false
      });
    };
    this.state = {
      top: null,
      left: null,
      rowHasFocus: false,
      rowHasMouse: false,
      rowHasOpenDropdown: false
    };
    this._getStyle = memoizeOne(getStyle);
  }
  componentDidMount() {
    requestAnimationFrame(() => {
      bindEventListeners(this);
      this.checkForOpenDropdown();
      if (this._el) {
        this._observer = new MutationObserver(this.checkForOpenDropdown);
        this._observer.observe(this._el, {
          attributes: true,
          childList: true,
          subtree: true
        });
      }
    });
  }
  componentDidUpdate(prevProps, prevState) {
    // In shrinkOnHide mode, compute our absolute position before hiding (#2412)
    if (this.props.shrinkOnHide && prevProps.open && !this.props.open) {
      const {
        top,
        left
      } = this.computeAbsolutePosition();
      if (top !== prevState.top || left !== prevState.left) {
        this.setState({
          top,
          left
        });
      }
    }
    const {
      onOpenChange
    } = this.props;
    const {
      rowHasFocus,
      rowHasMouse,
      rowHasOpenDropdown
    } = this.state;
    // These state checks prevent infinite update loops when `open` is controlled.
    if (rowHasFocus !== prevState.rowHasFocus || rowHasMouse !== prevState.rowHasMouse || rowHasOpenDropdown !== prevState.rowHasOpenDropdown) {
      const shouldBeOpen = rowHasFocus || rowHasMouse || rowHasOpenDropdown;
      onOpenChange(SyntheticEvent(shouldBeOpen));
    }
  }
  componentWillUnmount() {
    unbindEventListeners(this);
    if (this._observer) this._observer.disconnect();
  }
  computeAbsolutePosition() {
    // If we're visible but have shrinkOnHide, compute our absolute position.

    // Applying position: absolute implies display: block, so we set
    // display: block in the initial measurement to prevent discrepancies.
    this._el.classList.add('display-block');
    const {
      left,
      top
    } = this._el.getBoundingClientRect();
    this._el.classList.remove('display-block');

    // This measurement tells us how far position: absolute moves us,
    // with top and left set to 0.
    this._el.classList.add('private-table-hover-content--abs-pos');
    const {
      left: absLeft,
      top: absTop
    } = this._el.getBoundingClientRect();
    this._el.classList.remove('private-table-hover-content--abs-pos');

    // We take the difference between the two measurements as the appropriate
    // top and left values to apply while hidden.
    return {
      left: left - absLeft,
      top: top - absTop
    };
  }
  render() {
    const _this$props = this.props,
      {
        children,
        className,
        open,
        shrinkOnHide,
        style
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      top,
      left
    } = this.state;
    const computedClassName = classNames('table-hover-content', className);
    return /*#__PURE__*/_jsx(Span, Object.assign({
      open: open,
      shrinkOnHide: shrinkOnHide,
      className: computedClassName,
      ref: ref => {
        this._el = ref;
      },
      style: open || !shrinkOnHide ? style : this._getStyle(style, top, left)
    }, rest, {
      children: children
    }));
  }
}
UITableHoverContent.propTypes = {
  children: PropTypes.node.isRequired,
  onOpenChange: PropTypes.func,
  open: PropTypes.bool,
  shrinkOnHide: PropTypes.bool.isRequired
};
UITableHoverContent.defaultProps = {
  open: false,
  shrinkOnHide: false
};
UITableHoverContent.displayName = 'UITableHoverContent';
export default Controllable(UITableHoverContent, ['open']);